import { memo, useCallback, useMemo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CloseIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import { suggestionToMatchableItem } from "@components/accounting/transactionMatch/mappers";
import { MatchableItem } from "@components/accounting/transactionMatch/types";
import { AccrualMatchPreviewContent } from "@components/accounting/transactionMatch/AccrualMatchPreviewContent";
import {
    aggregateMatches,
    getSuggestedMatchPreviewItems,
} from "@components/accounting/transactionMatch/aggregateMatches";
import { AccrualMatchPreview } from "./AccrualMatchPreview";
import { useAccrualReviewContext } from "./useAccrualReviewContext";
import styles from "./AccrualMatchCell.module.scss";

export const AccrualMatchCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const transaction = row.original;
        const isTableReadonly = useIsTableReadonly(table);
        const { dismissMatch, isMatchDismissed } = useAccrualReviewContext();
        const { openOverlay } = useOverlays();
        const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
        const isReviewed = !!transaction.accrualAccountId;

        const suggestions = useMemo(
            () => transaction.accrualMatchSuggestions ?? [],
            [transaction.accrualMatchSuggestions],
        );

        const isDismissed = isMatchDismissed(transaction.id);

        const handleDismiss = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                if (refuseIfViewOnly()) {
                    return;
                }
                dismissMatch(transaction.id);
            },
            [dismissMatch, transaction.id, refuseIfViewOnly],
        );

        const handleOpenAccrualMatch = useCallback(() => {
            if (refuseIfViewOnly()) {
                return;
            }
            const activeSuggestions = isDismissed ? [] : suggestions;
            const suggestedItems: MatchableItem[] = activeSuggestions
                .map((s) => suggestionToMatchableItem(s, transaction.entity))
                .filter((item): item is MatchableItem => item != null);

            openOverlay("transactionMatch", {
                transactionId: transaction.id,
                suggestedItems:
                    suggestedItems.length > 0 ? suggestedItems : undefined,
            });
        }, [
            openOverlay,
            transaction.id,
            transaction.entity,
            suggestions,
            isDismissed,
            refuseIfViewOnly,
        ]);

        const previewItems = useMemo(
            () => getSuggestedMatchPreviewItems(suggestions),
            [suggestions],
        );

        const aggregated = useMemo(
            () => aggregateMatches(previewItems),
            [previewItems],
        );

        if (isReviewed) {
            return (
                <AccrualMatchPreviewContent
                    accrualMatches={transaction.accrualMatches ?? []}
                    onClick={handleOpenAccrualMatch}
                    readonly={isTableReadonly}
                />
            );
        }

        // Empty cell when nothing is renderable. `resolvedCount` covers
        // both an empty `previewItems` and the edge case where every entry
        // is unresolvable (no invoice, bill, or matched/paired transaction);
        // without this guard we'd render a populated pill showing "$0.00"
        // and "0 matches".
        if (isDismissed || aggregated.resolvedCount === 0) {
            return (
                <ViewTableCell captureClicks className={styles.cell}>
                    <EditableCellContent
                        isEmpty
                        onClick={handleOpenAccrualMatch}
                        readonly={isTableReadonly}
                    />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.cell}>
                <div className={styles.row}>
                    <EditableCellContent
                        className={styles.matchContent}
                        isEmpty={false}
                        readonly={isTableReadonly}
                        onClick={handleOpenAccrualMatch}
                    >
                        <AccrualMatchPreview
                            counterpartyName={aggregated.firstName}
                            extraCounterpartyCount={aggregated.extraNamesCount}
                            amount={aggregated.totalAmount}
                            countLabel={aggregated.countLabel}
                            isSuggested
                        />
                    </EditableCellContent>
                    {!isViewOnly && !isTableReadonly && (
                        <button
                            type="button"
                            className={classNames(
                                styles.removeButton,
                                viewTableStyles.showOnHover,
                            )}
                            onClick={handleDismiss}
                            aria-label="Remove suggested match"
                        >
                            <CloseIcon />
                        </button>
                    )}
                </div>
            </ViewTableCell>
        );
    },
);

AccrualMatchCell.displayName = "AccrualMatchCell";
